import type { Task } from "@app/core";

/** A task paired with its lowercased searchable haystack (title + notes +
 *  location). Build once per task list, reuse across keystrokes (see search.tsx). */
export interface SearchEntry {
  task: Task;
  hay: string;
}

/** Precompute the lowercased haystack for each task. Cheap to memoize on `tasks`
 *  so a per-keystroke query doesn't re-lowercase every task's text. */
export function buildSearchIndex(tasks: Task[]): SearchEntry[] {
  return tasks.map((t) => ({ task: t, hay: `${t.title}\n${t.notes}\n${t.location}`.toLowerCase() }));
}

/** Filter a prebuilt index: keep entries whose haystack contains EVERY whitespace
 *  term (AND), each a case-insensitive substring. An empty query matches nothing. */
export function searchIndex(index: SearchEntry[], query: string): Task[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  return index.filter((e) => terms.every((term) => e.hay.includes(term))).map((e) => e.task);
}

/**
 * In-memory full-text-ish search over task title + notes + location. Splits the
 * query into whitespace terms and keeps tasks matching ALL of them (AND), each
 * matched as a case-insensitive substring. An empty query matches nothing.
 *
 * Done in memory rather than via SQL because the MemoryAdapter has no LIKE verb
 * (see CLAUDE.md §7) — and search wants to span every active task regardless of
 * the calendar/label view filters.
 *
 * Convenience over {@link buildSearchIndex} + {@link searchIndex}; UI that filters
 * the same list repeatedly should memoize the index instead.
 */
export function searchTasks(tasks: Task[], query: string): Task[] {
  return searchIndex(buildSearchIndex(tasks), query);
}
